import React, { useState, useEffect } from 'react';
import { 
Container, 
Typography, 
Button, 
Paper, 
TextField,
Dialog,
DialogTitle,
DialogContent,
DialogActions,
IconButton,
Box,
Chip,
Divider,
useTheme,
Grid,
Slide,
MenuItem,
InputLabel,
FormControl,
Select,
TablePagination,
Menu,
TableContainer,
Table,
TableHead,
TableRow,
TableCell,
TableBody,
Card,
CardContent,
CircularProgress,
Tabs,
Tab,
Alert
} from '@mui/material';
import { 
Delete, 
Inventory,
PictureAsPdf,
TableChart, 
GetApp,
Add,
Settings,
TrendingUp,
Warning,
CheckCircle,
Error,
AttachMoney,
} from '@mui/icons-material';
import {
X,
Close as CloseIcon,
Add as AddIcon,
Search as SearchIcon,
FilterList as FilterIcon,
Refresh as RefreshIcon,
Today as TodayIcon,
Edit as EditIcon,
Visibility as ViewIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import axios from 'axios';
import useCustomToast from '../../../hooks/useToast';
import {
APIBarStockList,
APIBarStockStore,
APIBarStockUpdate,
APIBarStockDelete,
APIBarStockStats,
APIUnitesNames,
} from '../../../services/APIs';
import { TEXT_CONNECTION_ERROR, TEXT_DATA_LOADED } from '../../../services/Text';
import { 
formatCreatedAtDate, 
formatNumberWithComma, 
formatNumberWithComma2, 
getStatusById, 
getUserDetails, 
recordsPerPage, 
statusData 
} from '../../../utils/helpers';
import { exportToCSV, exportToExcel, exportToPDF } from '../../../services/bar/StockService';
import { getUserRole, PERMISSIONS, ROLE_PERMISSIONS } from '../../../utils/Permission';

const Stock = () => {
const theme = useTheme();
const [loading, setLoading] = useState(false);
const [statsLoading, setStatsLoading] = useState(false);
const [error, setError] = useState(null);
const showToast = useCustomToast();
const [updateId, setUpdateId] = useState('');
const [postStatus, setPostStatus] = useState('ADD');
const [exportAnchorEl, setExportAnchorEl] = useState(null);

const [pageState, setPageState] = useState({
    pageData: [],
    unitsData:[],
    stockStats: {
    total_stock_items: 0,
    total_quantity: 0,
    total_balance: 0,
    total_value: 0,
    total_selling_value: 0,
    active_stock: 0,
    out_of_stock: 0,
    low_stock: 0,
    normal_stock: 0,
    high_stock: 0,
    potential_profit: 0,
    most_valuable_item: 'N/A',
    most_valuable_value: 0
    }
});
const [page, setPage] = useState(0);
const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
const [selectedRecord, setSelectedRecord] = useState(null);
const [searchTerm, setSearchTerm] = useState('');

const [formData, setFormData] = useState({
    name: '',
    quantity: '',
    description: '',
    uom: '',
    cost: '',
    selling: '',
    stock_date: '',
    status: '',
    notes: ''
});

const [filters, setFilters] = useState({
    stock_level: '',
    status: '',
    dateFrom: '',
    dateTo: ''
});

// State for dialogs
const [filterOptions, setFilterOptions] = useState(false);
const [openAdd, setOpenAdd] = useState(false);
const [openView, setOpenView] = useState(false);
const [openDelete, setOpenDelete] = useState(false);

// Tab state for Add/Edit dialog
const [activeTab, setActiveTab] = useState(0);

// Permissions
const [userRole, setUserRole] = useState('');
useEffect(() => {
    const role = getUserRole();
    setUserRole(role);
}, []);

const hasPermission = (permission) => {
    return ROLE_PERMISSIONS[userRole]?.includes(permission) || false;
};

useEffect(() => {
    fetchData();
}, []);

const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
    const branchId = getUserDetails("branchId");

    const params = {};
    if (branchId) {
        params.branch_id = branchId;
    }

    const [pageRes, statsRes, uniRes] = await Promise.all([
        axios.get(APIBarStockList, { params }),
        axios.get(APIBarStockStats, { params }),
        axios.get(APIUnitesNames),
    ]);

    const stocks = Array.isArray(pageRes.data.data) ? pageRes.data.data.reverse() : [];
    const units = Array.isArray(uniRes.data.data) ? uniRes.data.data.reverse() : [];

    setPageState(prev => ({
        ...prev,
        pageData: stocks,
        unitsData: units,
        stockStats: statsRes.data.status === 'success' ? statsRes.data.data : prev.stockStats
    }));

    showToast("Success", TEXT_DATA_LOADED, "success");
    } catch (error) {
    let errorMessage = "Failed to load data";

    if (error.response) {
        errorMessage = error.response.data.message || error.message;
    }
    else if (error.code === "ERR_NETWORK") {
        errorMessage = TEXT_CONNECTION_ERROR;
    }

    setError(errorMessage);
    console.log(errorMessage);
    showToast("Error", errorMessage, "error");
    } finally {
    setLoading(false);
    }
};

const fetchStockStats = async () => {
    setStatsLoading(true);
    try {
    const branchId = getUserDetails("branchId");

    const params = {};
    if (branchId) {
        params.branch_id = branchId;
    }

    const response = await axios.get(APIBarStockStats, { params });
    if (response.data.status === 'success') {
        setPageState(prev => ({
        ...prev,
        stockStats: response.data.data
        }));
    }
    } catch (error) {
    console.error("Error fetching stock stats:", error);
    } finally {
    setStatsLoading(false);
    }
};

// Helper function to get stock level info
const getStockLevelInfo = (balance) => {
    if (balance <= 0) {
    return { level: 'Out of Stock', color: theme.palette.error.main, bgColor: theme.palette.error.light };
    } else if (balance <= 10) {
    return { level: 'Low', color: theme.palette.warning.main, bgColor: theme.palette.warning.light };
    } else if (balance <= 50) {
    return { level: 'Normal', color: theme.palette.success.main, bgColor: theme.palette.success.light };
    } else {
    return { level: 'High', color: theme.palette.info.main, bgColor: theme.palette.info.light };
    }
};

// Filter stocks based on search term and filters
const filteredData = pageState.pageData.filter(stock => {
    const matchesSearch = searchTerm === '' ||
    stock.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    stock.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    stock.uom.toLowerCase().includes(searchTerm.toLowerCase());

    // Stock level filter
    const matchesStockLevel = !filters.stock_level ||
    getStockLevelInfo(stock.balance).level === filters.stock_level;

    // Status filter
    const matchesStatus = !filters.status ||
    stock.status === filters.status;

    // Date range filter
    let matchesDate = true;
    if (filters.dateFrom) {
    const stockDate = new Date(stock.stock_date).toISOString().split('T')[0];
    const filterFromDate = new Date(filters.dateFrom).toISOString().split('T')[0];
    matchesDate = matchesDate && stockDate >= filterFromDate;
    }

    if (filters.dateTo) {
    const stockDate = new Date(stock.stock_date).toISOString().split('T')[0];
    const filterToDate = new Date(filters.dateTo).toISOString().split('T')[0];
    matchesDate = matchesDate && stockDate <= filterToDate;
    }

    return matchesSearch && matchesStockLevel && matchesStatus && matchesDate;
});

// Pagination handlers
const handleChangePage = (event, newPage) => {
    setPage(newPage);
};

const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
};

// Dialog handlers
const handlefilterOptions = () => setFilterOptions(true);
const handleCloseOptions = () => setFilterOptions(false);

const handleOpenAdd = () => {
    setPostStatus("ADD");
    setFormData({
    name: '',
    quantity: '',
    description: '',
    uom: '',
    cost: '',
    selling: '',
    stock_date: new Date().toISOString().split('T')[0],
    status: '',
    notes: ''
    });
    setActiveTab(0);
    setOpenAdd(true);
};

const handleCloseAdd = () => {
    setOpenAdd(false);
    setActiveTab(0);
};

const handleOpenView = (record) => {
    setSelectedRecord(record);
    setOpenView(true);
};

const handleCloseView = () => setOpenView(false);

const handleOpenEdit = (record) => {
    setSelectedRecord(record);
    setUpdateId(record.id);
    setPostStatus("UPDATE");
    setFormData({
    name: record.name,
    quantity: record.quantity,
    description: record.description || '',
    uom: record.uom,
    cost: record.cost,
    selling: record.selling,
    stock_date: new Date(record.stock_date).toISOString().split('T')[0],
    status: record.status,
    notes: record.notes || ''
    });
    setActiveTab(0);
    setOpenAdd(true);
};

const handleOpenDelete = (record) => {
    setSelectedRecord(record);
    setUpdateId(record.id);
    setPostStatus("DELETE");
    setOpenDelete(true);
};

const handleCloseDelete = () => {
    setOpenDelete(false);
};

// Form handlers
const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
    ...prev,
    [name]: value
    }));
};

// Filter handlers
const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
    ...prev,
    [name]: value
    }));
};

const applyTodayFilter = () => {
    const today = new Date().toISOString().split('T')[0];
    setFilters({
    ...filters,
    dateFrom: today,
    dateTo: today
    });
    handleCloseOptions();
};

const clearFilters = () => {
    setFilters({
    stock_level: '',
    status: '',
    dateFrom: '',
    dateTo: ''
    });
    setSearchTerm('');
    handleCloseOptions();
};

// ============================ Export handlers ====================
const handleExportClick = (event) => {
    setExportAnchorEl(event.currentTarget);
};

const handleExportClose = () => {
    setExportAnchorEl(null);
};

const handleExportExcel = async () => {
    try {
    handleExportClose();
    await exportToExcel(filteredData, [], filters);
    showToast("Success", "Excel file downloaded successfully", "success");
    } catch (error) {
    console.error('Excel export error:', error);
    showToast("Error", error.message, "error");
    }
};

const handleExportPDF = async () => {
    try {
    handleExportClose();
    await exportToPDF(filteredData, [], filters);
    showToast("Success", "PDF file downloaded successfully", "success");
    } catch (error) {
    console.error('PDF export error:', error);
    showToast("Error", error.message, "error");
    }
};

const handleExportCSV = async () => {
    try {
    handleExportClose();
    await exportToCSV(filteredData, []);
    showToast("Success", "CSV file downloaded successfully", "success");
    } catch (error) {
    console.error('CSV export error:', error);
    showToast("Error", error.message, "error");
    }
};
// ============================ Export handlers end ====================

// Tab navigation
const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
};

const handleNextTab = () => {
    if (activeTab < 2) {
    setActiveTab(activeTab + 1);
    }
};

const handlePrevTab = () => {
    if (activeTab > 0) {
    setActiveTab(activeTab - 1);
    }
};

const validateForm = () => {
    const errors = [];

    if (!formData.name.trim()) errors.push('Name is required');
    if (!formData.uom.trim()) errors.push('Unit of Measurement is required');
    if (!formData.stock_date) errors.push('Stock date is required');
    if (!formData.status) errors.push('Status is required');

    if (errors.length > 0) {
    showToast("Validation Error", errors.join('\n'), "warning");
    return false;
    }
    return true;
};

const handleSubmit = async (e) => {
    e.preventDefault();
    if (postStatus !== "DELETE") {
    if (!validateForm()) return;
    }

    const formDataToSend = new FormData();
    
    // Set branch_id from user details
    formDataToSend.append('branch_id', getUserDetails("branchId"));
    
    // Add user_id (current user)
    formDataToSend.append('user_id', getUserDetails("Id"));
    
    // Add form data
    formDataToSend.append('name', formData.name);
    formDataToSend.append('quantity', formData.quantity);
    formDataToSend.append('description', formData.description || '');
    formDataToSend.append('uom', formData.uom);
    formDataToSend.append('cost', formData.cost);
    formDataToSend.append('selling', formData.selling);
    formDataToSend.append('stock_date', formData.stock_date);
    formDataToSend.append('status', formData.status);
    formDataToSend.append('notes', formData.notes || '');

    // Only append updateId for UPDATE/DELETE operations
    if (postStatus === "UPDATE" || postStatus === "DELETE") {
    formDataToSend.append('updateId', updateId);
    }

    try {
    let apiUrl, method;
    if (postStatus === "ADD") {
        apiUrl = APIBarStockStore;
        method = 'post';
    } else if (postStatus === "UPDATE") {
        apiUrl = APIBarStockUpdate;
        method = 'post';
    } else if (postStatus === "DELETE") {
        apiUrl = APIBarStockDelete;
        method = 'post';
    }

    const response = await axios({
        method,
        url: apiUrl,
        data: formDataToSend
    });

    if (response.data.status === "success") {
        showToast("Success", response.data.message, "success");
        handleCloseAdd();
        fetchData();
        fetchStockStats();
    } else {
        showToast("Warning", response.data.message || "Operation completed with warnings", "warning");
    }
    } catch (error) {
    if (error.response) {
        const { status, data } = error.response;
        if (status === 422) {
        const errors = data.errors || {};
        let errorMessages = [];
        for (const field in errors) {
            errorMessages.push(`${field}: ${errors[field].join(', ')}`);
        }
        showToast(
            "Validation Error",
            errorMessages.join('\n') || "Please correct the form errors",
            "error"
        );
        } else if (status === 409) {
        showToast("Error", data.message || "This record already exists", "error");
        } else if (status >= 500) {
        showToast("Server Error", data.message || "Internal server error", "error");
        } else {
        showToast("Error", data.message || "Request failed", "error");
        }
    } else if (error.request) {
        showToast("Network Error", "No response from server. Please check your connection.", "error");
    } else {
        showToast("Error", error.message || "An unexpected error occurred", "error");
    }
    console.error("API Error:", error);
    }
};

if (loading) return <div>Loading...</div>;
if (error) return <div>Error: {error}</div>;

return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
    <Typography sx={{
        fontWeight: 'bold',
        color: theme.palette.primary.main,
        flexShrink: 0,
        width: { xs: '100%', sm: 'auto' },
        mb: { xs: 1, sm: 0 }
    }}>
        Bar Stock Management ( {formatNumberWithComma2(pageState.pageData.length)} Total Items )
    </Typography>

    {/* Stock Statistics Cards */}
    <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Total Stock Items */}
        <Grid item xs={12} sm={6} md={4} lg={2}>
        <Card elevation={3} sx={{ 
            borderRadius: 3,
            bgcolor: 'primary.main',
            color: 'white',
            height: '100%'
        }}>
            <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Inventory sx={{ fontSize: 30, mr: 2 }} />
                <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Total Items
                </Typography>
                <Typography $ sx={{ fontWeight: 'bold' }}>
                    {statsLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 
                    formatNumberWithComma(pageState.stockStats.total_stock_items || 0)}
                </Typography>
                </Box>
            </Box>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                All stock items
            </Typography>
            </CardContent>
        </Card>
        </Grid>

        {/* Current Balance */}
        <Grid item xs={12} sm={6} md={4} lg={2}>
        <Card elevation={3} sx={{ 
            borderRadius: 3,
            bgcolor: 'success.main',
            color: 'white',
            height: '100%'
        }}>
            <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CheckCircle sx={{ fontSize: 30, mr: 2 }} />
                <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Current Balance
                </Typography>
                <Typography $ sx={{ fontWeight: 'bold' }}>
                    {statsLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 
                    formatNumberWithComma(pageState.stockStats.total_balance || 0)}
                </Typography>
                </Box>
            </Box>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Total items available
            </Typography>
            </CardContent>
        </Card>
        </Grid>

        {/* Low Stock Alert */}
        <Grid item xs={12} sm={6} md={4} lg={2}>
        <Card elevation={3} sx={{ 
            borderRadius: 3,
            bgcolor: pageState.stockStats.low_stock > 0 ? 'warning.dark' : 'success.dark',
            color: 'white',
            height: '100%'
        }}>
            <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Warning sx={{ fontSize: 30, mr: 2 }} />
                <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Low Stock Items
                </Typography>
                <Typography $ sx={{ fontWeight: 'bold' }}>
                    {statsLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 
                    formatNumberWithComma(pageState.stockStats.low_stock || 0)}
                </Typography>
                </Box>
            </Box>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Items with stock ≤ 10
            </Typography>
            </CardContent>
        </Card>
        </Grid>

        {/* Out of Stock */}
        <Grid item xs={12} sm={6} md={4} lg={2}>
        <Card elevation={3} sx={{ 
            borderRadius: 3,
            bgcolor: pageState.stockStats.out_of_stock > 0 ? 'error.dark' : 'success.dark',
            color: 'white',
            height: '100%'
        }}>
            <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Error sx={{ fontSize: 30, mr: 2 }} />
                <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Out of Stock
                </Typography>
                <Typography $ sx={{ fontWeight: 'bold' }}>
                    {statsLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 
                    formatNumberWithComma(pageState.stockStats.out_of_stock || 0)}
                </Typography>
                </Box>
            </Box>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Items with 0 stock
            </Typography>
            </CardContent>
        </Card>
        </Grid>
    </Grid>

    <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        {/* Buttons - column on mobile, row on desktop */}
        <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            flexShrink: 0,
            order: { xs: 2, sm: 1 }
        }}>
            {hasPermission(PERMISSIONS.ADD) && (
            <Button
                variant="contained"
                color="warning"
                startIcon={<AddIcon />}
                onClick={handleOpenAdd}
                sx={{
                textTransform: 'none',
                borderRadius: '8px'
                }}
            >
                Add New Stock
            </Button>
            )}
    
            <Button
            variant="contained"
            color="secondary"
            startIcon={<FilterIcon />}
            onClick={handlefilterOptions}
            sx={{
                textTransform: 'none',
                borderRadius: '8px'
            }}
            >
            View Options
            </Button>

            {hasPermission(PERMISSIONS.EXPORT) && (
            <Button
                variant="contained"
                color="primary"
                startIcon={<GetApp />}
                onClick={handleExportClick}
                sx={{
                textTransform: 'none',
                borderRadius: '8px'
                }}
            >
                Export Data
            </Button>
            )}

            <Button
            variant="outlined"
            color="info"
            startIcon={<RefreshIcon />}
            onClick={() => {
                fetchData();
                fetchStockStats();
            }}
            sx={{
                textTransform: 'none',
                borderRadius: '8px'
            }}
            >
            Refresh
            </Button>
        </Box>

        {/* Search Bar - full width on mobile, fixed on desktop */}
        <Paper sx={{
            width: { xs: '100%', sm: '300px' },
            p: '2px 8px',
            display: 'flex',
            alignItems: 'center',
            marginLeft: { xs: 0, sm: 'auto' },
            order: { xs: 1, sm: 2 },
            borderRadius: '8px',
            background: theme.palette.primary.light
        }}>
            <TextField
            fullWidth
            variant="outlined"
            placeholder="Search stock..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'white' }} />,
                sx: {
                '& fieldset': { border: 'none' },
                fontSize: '0.875rem',
                color: 'white',
                '& input': {
                    color: 'white',
                    '&::placeholder': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    opacity: 1
                    }
                }
                }
            }}
            size="small"
            />
        </Paper>
        </Box>

        {/* Active Filters as a separate row below */}
        {( filters.stock_level || filters.status || filters.dateFrom || filters.dateTo) && (
        <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            flexWrap: 'wrap',
            alignItems: 'center',
            mt: 2,
            pt: 2,
            borderTop: '1px solid',
            borderColor: 'divider'
        }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
            Active filters:
            </Typography>
            {filters.stock_level && (
            <Chip
                label={`Stock Level: ${filters.stock_level}`}
                onDelete={() => setFilters(prev => ({ ...prev, stock_level: '' }))}
                color="primary"
                variant="outlined"
            />
            )}
            {filters.status && (
            <Chip
                label={`Status: ${getStatusById(filters.status)}`}
                onDelete={() => setFilters(prev => ({ ...prev, status: '' }))}
                color="primary"
                variant="outlined"
            />
            )}
            {filters.dateFrom && (
            <Chip 
                label={`From: ${new Date(filters.dateFrom).toLocaleDateString()}`}
                onDelete={() => setFilters(prev => ({ ...prev, dateFrom: '' }))}
                color="primary"
                variant="outlined"
            />
            )}
            {filters.dateTo && (
            <Chip 
                label={`To: ${new Date(filters.dateTo).toLocaleDateString()}`}
                onDelete={() => setFilters(prev => ({ ...prev, dateTo: '' }))}
                color="primary"
                variant="outlined"
            />
            )}
            <Button 
            size="small"
            onClick={clearFilters}
            startIcon={<CloseIcon />}
            sx={{ ml: 1 }}
            >
            Clear All
            </Button>
        </Box>
        )}
    </Paper>

    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
    >
        <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <TableContainer>
            <Table>
            <TableHead>
                <TableRow sx={{
                backgroundColor: theme.palette.primary.main,
                '& th': {
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.95rem'
                }
                }}>
                <TableCell>#</TableCell>
                <TableCell>Stock Date</TableCell>
                <TableCell>Item Name</TableCell>
                <TableCell>Balance</TableCell>
                <TableCell>Cost</TableCell>
                <TableCell>Selling</TableCell>
                <TableCell>Stock Level</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((stock, index) => {
                const stockLevel = getStockLevelInfo(stock.balance);
                return (
                    <TableRow
                    key={index}
                    hover
                    sx={{
                        '&:nth-of-type(odd)': {
                        backgroundColor: theme.palette.action.hover
                        }
                    }}
                    >
                    <TableCell>
                        <Chip
                        label={page * rowsPerPage + index + 1}
                        color="primary"
                        size="small"
                        />
                    </TableCell>
                    <TableCell>
                        {new Date(stock.stock_date).toLocaleDateString()}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'medium' }}>
                        {stock.name}
                    </TableCell>
                    <TableCell>
                        <Chip
                        label={formatNumberWithComma2(stock.balance)}
                        color={stock.balance > 0 ? "success" : "error"}
                        variant="filled"
                        size="small"
                        />
                    </TableCell>
                    <TableCell>
                        <Chip
                        label={formatNumberWithComma(stock.cost)}
                        color="success"
                        variant="outlined"
                        size="small"
                        />
                    </TableCell>
                    <TableCell>
                        <Chip
                        label={formatNumberWithComma(stock.selling)}
                        color="primary"
                        variant="outlined"
                        size="small"
                        />
                    </TableCell>
                    <TableCell>
                        <Chip
                        label={stockLevel.level}
                        sx={{
                            backgroundColor: stockLevel.bgColor,
                            color: 'white',
                            fontWeight: 'bold'
                        }}
                        size="small"
                        />
                    </TableCell>
                    <TableCell>
                        {stock.status === 1 ? (
                        <Chip
                            label={getStatusById(stock.status)}
                            size="small"
                            color="primary"
                        />
                        ) : (
                        <Chip
                            label={getStatusById(stock.status)}
                            size="small"
                            color="error"
                        />
                        )}
                    </TableCell>
                    <TableCell align="center">
                        <IconButton
                        edge="end"
                        onClick={() => handleOpenView(stock)}
                        color="primary"
                        sx={{
                            '&:hover': {
                            backgroundColor: theme.palette.info.light
                            }
                        }}
                        >
                        <ViewIcon />
                        </IconButton>
                        {hasPermission(PERMISSIONS.EDIT) && (<>
                        <IconButton
                            edge="end"
                            onClick={() => handleOpenEdit(stock)}
                            color="secondary"
                            sx={{
                            '&:hover': {
                                backgroundColor: theme.palette.success.light
                            }
                            }}
                        >
                            <EditIcon />
                        </IconButton>
                        </>)}
                        {hasPermission(PERMISSIONS.DELETE) && (<>
                        <IconButton
                            edge="end"
                            onClick={() => handleOpenDelete(stock)}
                            color="error"
                            sx={{
                            '&:hover': {
                                backgroundColor: theme.palette.error.light
                            }
                            }}
                        >
                            <Delete />
                        </IconButton>
                        </>)}
                    </TableCell>
                    </TableRow>
                );
                })}
            </TableBody>
            </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ mt: 2 }}
        />

        {filteredData.length === 0 && (
            <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 8,
            color: 'text.secondary'
            }}>
            <Inventory sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h6">No stock items found</Typography>
            <Typography variant="body2">Try adjusting your search query or add a new stock item</Typography>
            </Box>
        )}
        </Paper>
    </motion.div>

    {/* Export Menu */}
    <Menu
        anchorEl={exportAnchorEl}
        open={Boolean(exportAnchorEl)}
        onClose={handleExportClose}
        anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
        }}
        transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
        }}
    >
        <MenuItem onClick={handleExportExcel} sx={{ gap: 1 }}>
        <TableChart fontSize="small" color="success" />
        Export to Excel (.xlsx)
        </MenuItem>
        <MenuItem onClick={handleExportPDF} sx={{ gap: 1 }}>
        <PictureAsPdf fontSize="small" color="error" />
        Export to PDF (.pdf)
        </MenuItem>
        <MenuItem onClick={handleExportCSV} sx={{ gap: 1 }}>
        <TableChart fontSize="small" color="info" />
        Export to CSV (.csv)
        </MenuItem>
    </Menu>

    {/* Options Dialog */}
    <Dialog
        open={filterOptions}
        onClose={handleCloseOptions}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="sm"
    >
        <DialogTitle sx={{ textAlign: 'center' }}>Filter Options</DialogTitle>
        <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
            <Button
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={clearFilters}
            sx={{ textTransform: 'none' }}
            >
            Show All Stock
            </Button>

            <Button
            variant="outlined"
            color="primary"
            startIcon={<TodayIcon />}
            onClick={applyTodayFilter}
            sx={{ textTransform: 'none' }}
            >
            Show Today's Stock
            </Button>

            <Divider>OR</Divider>

            <FormControl fullWidth>
            <InputLabel>Stock Level</InputLabel>
            <Select
                name="stock_level"
                value={filters.stock_level}
                onChange={handleFilterChange}
                label="Stock Level"
            >
                <MenuItem value="">All Levels</MenuItem>
                <MenuItem value="Out of Stock">Out of Stock</MenuItem>
                <MenuItem value="Low">Low Stock</MenuItem>
                <MenuItem value="Normal">Normal Stock</MenuItem>
                <MenuItem value="High">High Stock</MenuItem>
            </Select>
            </FormControl>

            <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                label="Status"
            >
                <MenuItem value="">All Status</MenuItem>
                {statusData.map((item, index) => (
                <MenuItem key={index} value={item.id}>{item.name}</MenuItem>
                ))}
            </Select>
            </FormControl>

            <Divider>OR</Divider>

            <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
            Filter by Stock Date Range
            </Typography>

            <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
                <TextField
                fullWidth
                label="From Date"
                type="date"
                name="dateFrom"
                value={filters.dateFrom}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                fullWidth
                label="To Date"
                type="date"
                name="dateTo"
                value={filters.dateTo}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
                />
            </Grid>
            </Grid>
        </Box>
        </DialogContent>
        <DialogActions>
        <Button
            color="error"
            onClick={handleCloseOptions}
            variant="contained"
        >
            Close
        </Button>
        <Button
            onClick={handleCloseOptions}
            color="primary"
            variant="contained"
        >
            Apply Filters
        </Button>
        </DialogActions>
    </Dialog>

    {/* Add/Edit Dialog with Tabs */}
    <Dialog
        open={openAdd}
        onClose={handleCloseAdd}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="sm"
        scroll="paper"
    >
        <DialogTitle sx={{ 
        textAlign: 'center',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pr: 1
        }}>
        <Typography variant="h6" sx={{ fontWeight: 'semibold', flex: 1 }}>
            {postStatus === "ADD" ? "Add New Stock Item" : "Edit Stock Item"}
        </Typography>
        <IconButton
            edge="end"
            color="inherit"
            onClick={handleCloseAdd}
            aria-label="close"
            size="small"
        >
            <X />
        </IconButton>
        </DialogTitle>
        <DialogContent dividers>
        <Box component="form" onSubmit={handleSubmit}>
            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs 
                value={activeTab} 
                onChange={handleTabChange}
                variant="fullWidth"
            >
                <Tab 
                label="Basic Info" 
                sx={{ textTransform: 'none' }}
                />
                <Tab 
                label="Pricing" 
                sx={{ textTransform: 'none' }}
                />
                <Tab 
                label="Settings" 
                sx={{ textTransform: 'none' }}
                />
            </Tabs>
            </Box>

            {/* Tab 1: Basic Info */}
            {activeTab === 0 && (
            <Box>
                <Box>
                <br/>
                <Grid item xs={12}>
                    <TextField
                    fullWidth
                    label="Item Name *"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    />
                </Grid>
                <br/>
                <Grid item xs={12}>
                    <FormControl fullWidth required>
                    <InputLabel>Unit of Measurement (UOM) *</InputLabel>
                    <Select
                        name="uom"
                        value={formData.uom}
                        onChange={handleInputChange}
                        label="Status *"
                    >
                        {pageState.unitsData.map((item, index) => (
                        <MenuItem key={index} value={item.short_name}>{item.short_name}</MenuItem>
                        ))}
                    </Select>
                    </FormControl>
                </Grid>
                <br/>
                <Grid item xs={12}>
                    <TextField
                    fullWidth
                    label="Stock Date *"
                    type="date"
                    name="stock_date"
                    value={formData.stock_date}
                    onChange={handleInputChange}
                    InputLabelProps={{ shrink: true }}
                    required
                    />
                </Grid>
                <br/>
                <Grid item xs={12}>
                    <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    />
                </Grid>
                </Box>
            </Box>
            )}

            {/* Tab 2: Pricing */}
            {activeTab === 1 && (
                <Box>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Quantity *"
                                name="quantity"
                                type="number"
                                value={formData.quantity}
                                onChange={handleInputChange}
                                inputProps={{ min: 1 }}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Cost Price *"
                                name="cost"
                                type="number"
                                value={formData.cost}
                                onChange={handleInputChange}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Selling Price *"
                                name="selling"
                                type="number"
                                value={formData.selling}
                                onChange={handleInputChange}
                                required
                            />
                        </Grid>
                    </Grid>
                    {postStatus ==="ADD" && (<>
                        <Grid item xs={12}>
                            <Alert severity="info" sx={{ mt: 2 }}>
                                <Typography variant="body2">
                                    <strong><b>
                                    The Quantity, Selling, Buying Prices Will Be Set When Item Inventory Is Made
                                    </b> </strong>
                                </Typography>
                            </Alert>
                        </Grid>
                    </>)}
                </Box>
            )}

            {/* Tab 3: Settings */}
            {activeTab === 2 && (
            <Box>
                <Box>
                <Grid item xs={12}>
                    <FormControl fullWidth required>
                    <InputLabel>Status *</InputLabel>
                    <Select
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                        label="Status *"
                    >
                        {statusData.map((item, index) => (
                        <MenuItem key={index} value={item.id}>{item.name}</MenuItem>
                        ))}
                    </Select>
                    </FormControl>
                </Grid>
                <br/>
                <Grid item xs={12}>
                    <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    />
                </Grid>
                </Box>
            </Box>
            )}
        </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Box>
            {activeTab > 0 && (
            <Button
                color="primary"
                onClick={handlePrevTab}
                variant="outlined"
                sx={{ mr: 1 }}
            >
                Previous
            </Button>
            )}
        </Box>
        <Box>
            <Button
            color="error"
            onClick={handleCloseAdd}
            variant="contained"
            sx={{ mr: 1 }}
            >
            Cancel
            </Button>
            {activeTab < 2 ? (
            <Button
                color="primary"
                onClick={handleNextTab}
                variant="contained"
            >
                Next
            </Button>
            ) : (
            <Button
                type="submit"
                color="primary"
                variant="contained"
                onClick={handleSubmit}
            >
                {postStatus === "ADD" ? "Add Stock" : "Update Stock"}
            </Button>
            )}
        </Box>
        </DialogActions>
    </Dialog>

    {/* View Dialog */}
    <Dialog
        open={openView}
        onClose={handleCloseView}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="sm"
    >
        <DialogTitle sx={{ textAlign: 'center' }}><b>Stock Item Details</b></DialogTitle>
        <DialogContent>
        {selectedRecord && (
            <Box sx={{ p: 2 }}>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                <Typography variant="subtitle2"><b>Item Name:</b> {selectedRecord.name}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2"><b>UOM:</b> {selectedRecord.uom}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2"><b>Current Balance:</b> {formatNumberWithComma(selectedRecord.balance)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2"><b>Cost Price:</b> {formatNumberWithComma(selectedRecord.cost)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2"><b>Selling Price:</b> {formatNumberWithComma(selectedRecord.selling)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2"><b>Total Cost:</b> {formatNumberWithComma(selectedRecord.total_cost)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2"><b>Total Selling:</b> {formatNumberWithComma(selectedRecord.total_selling)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2"><b>Stock Date:</b> {new Date(selectedRecord.stock_date).toLocaleDateString()}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2"><b>Stock Level:</b>
                    <Chip
                    label={getStockLevelInfo(selectedRecord.balance).level}
                    sx={{
                        ml: 1,
                        backgroundColor: getStockLevelInfo(selectedRecord.balance).bgColor,
                        color: 'white',
                        fontWeight: 'bold'
                    }}
                    size="small"
                    />
                </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2"><b>Status:</b>
                    {selectedRecord.status === 1 ? (
                    <Chip
                        label={getStatusById(selectedRecord.status)}
                        size="small"
                        color="primary"
                        sx={{ ml: 1 }}
                    />
                    ) : (
                    <Chip
                        label={getStatusById(selectedRecord.status)}
                        size="small"
                        color="error"
                        sx={{ ml: 1 }}
                    />
                    )}
                </Typography>
                </Grid>
            </Grid>
            <Box>
                <br/>
                {selectedRecord.description && (
                <Grid item xs={12}>
                    <Typography variant="subtitle2"><b>Description:</b> {selectedRecord.description}</Typography>
                </Grid>
                )}
                <br/>
                {selectedRecord.notes && (
                <Grid item xs={12}>
                    <Typography variant="subtitle2"><b>Notes:</b> {selectedRecord.notes}</Typography>
                </Grid>
                )}
                <br/>
                <Grid item xs={12}>
                <Typography variant="subtitle2"><b>Created Date:</b> {formatCreatedAtDate(selectedRecord.created_at)}</Typography>
                </Grid>
            </Box>
            </Box>
        )}
        </DialogContent>
        <DialogActions>
        <Button
            color="error"
            onClick={handleCloseView}
            variant="contained"
        >
            Close
        </Button>
        </DialogActions>
    </Dialog>

    {/* Delete Confirmation Dialog */}
    <Dialog
        open={openDelete}
        onClose={handleCloseDelete}
        TransitionComponent={Transition}
    >
        <DialogTitle sx={{ textAlign: 'center' }}>Delete Stock Item</DialogTitle>
        <DialogContent>
        <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
            Are you sure you want to delete this stock item?
        </Typography>
        {selectedRecord && (
            <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {selectedRecord.name}
            </Typography>
            <Typography variant="body2">
                Current Balance: {formatNumberWithComma(selectedRecord.balance)} {selectedRecord.uom}
            </Typography>
            {selectedRecord.balance > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                This item still has stock balance. Deleting it may cause inventory discrepancies.
                </Alert>
            )}
            </Box>
        )}
        </DialogContent>
        <DialogActions>
        <Button
            color="error"
            onClick={handleCloseDelete}
            variant="contained"
        >
            Cancel
        </Button>
        <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
        >
            Confirm Delete
        </Button>
        </DialogActions>
    </Dialog>
    </Container>
);
};

export default Stock;

const Transition = React.forwardRef(function Transition(props, ref) {
return <Slide direction="up" ref={ref} {...props} />;
});